import { useState } from 'react';
import { ArrowSquareOut, Copy } from '@phosphor-icons/react';
import { getAsset, NETWORKS, type ActivityDTO, type AssetCode } from '@onyx/shared';
import { useActivity, useActivityItem, useMoneyMutation } from '../lib/queries';
import { useLang, useT, errorText } from '../lib/i18n';
import { cryptoParts, displayDigits, formatCrypto, formatDateTime, formatDay, formatTime } from '../lib/format';
import { navigate, back } from '../lib/router';
import { api, ApiError } from '../lib/api';
import { copyText, openExternal, haptic } from '../lib/telegram';
import { AssetChip } from '../components/AssetChip';
import { ErrorState, Facts, Money, Row, Screen, ScreenHeader, Segmented, Skeleton, Slab, toast } from '../components/ui';

const FILTERS = [
  { id: 'all', types: '' },
  { id: 'in', types: 'deposit' },
  { id: 'out', types: 'withdraw' },
  { id: 'transfers', types: 'transfer_in,transfer_out' },
  { id: 'swaps', types: 'swap' },
  { id: 'cheques', types: 'cheque_create,cheque_claim,cheque_refund,invoice_pay,invoice_receive' },
] as const;

type FilterId = (typeof FILTERS)[number]['id'];

function counterpartyName(a: ActivityDTO): string | null {
  if (!a.counterparty) return null;
  return a.counterparty.username ? `@${a.counterparty.username}` : a.counterparty.firstName;
}

export function activityTitle(a: ActivityDTO, t: ReturnType<typeof useT>): string {
  const who = counterpartyName(a);
  const base = t[`type.${a.type}`];
  if (a.type === 'swap') return `${base} ${a.asset} → ${a.asset2}`;
  if (a.type === 'deposit' || a.type === 'withdraw') return `${base} · ${a.network ? NETWORKS[a.network].name : ''}`;
  return who && ['transfer_in', 'transfer_out', 'cheque_claim', 'invoice_receive'].includes(a.type) ? `${base} ${who}` : base;
}

function signedParts(amount: string, asset: AssetCode, lang: 'ru' | 'en') {
  const n = Number(amount);
  const p = cryptoParts(amount, displayDigits(n, !!getAsset(asset)?.stable), lang);
  return { ...p, sign: n > 0 ? '+' : p.sign };
}

/** `readOnly` rows (admin viewing someone else's history) do not open the detail screen. */
export function ActivityRow({ item, readOnly = false }: { item: ActivityDTO; readOnly?: boolean }) {
  const t = useT();
  const lang = useLang();
  const incoming = Number(item.amount) > 0;
  const settled = item.status === 'completed';
  return (
    <Row
      className={`activity activity--${item.status}`}
      onClick={readOnly ? undefined : () => navigate(`/activity/${item.id}`)}
      leading={<AssetChip asset={item.type === 'swap' && item.asset2 ? item.asset2 : item.asset} size={36} />}
      title={activityTitle(item, t)}
      subtitle={
        <span className="num">
          {formatTime(item.createdAt, lang)} · <span className="activity__code">{item.code}</span>
        </span>
      }
      trailing={
        item.type === 'swap' && item.amount2 && item.asset2 ? (
          <Money parts={signedParts(item.amount2, item.asset2, lang)} unit={item.asset2} size="sm" className="is-in" />
        ) : (
          <Money parts={signedParts(item.amount, item.asset, lang)} unit={item.asset} size="sm" className={incoming && settled ? 'is-in' : ''} />
        )
      }
      trailingSub={
        !settled ? (
          <span className={`status status--${item.status}`}>{t[`status.${item.status}`]}</span>
        ) : item.type === 'swap' ? (
          <span className="num">
            {formatCrypto(item.amount, 6, lang)} {item.asset}
          </span>
        ) : undefined
      }
    />
  );
}

export function History() {
  const t = useT();
  const lang = useLang();
  const [filter, setFilter] = useState<FilterId>('all');
  const types = FILTERS.find((f) => f.id === filter)!.types;
  const q = useActivity({ types: types || undefined });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  const groups: Array<{ day: string; items: ActivityDTO[] }> = [];
  for (const it of items) {
    const day = formatDay(it.createdAt, lang, t['history.today'], t['history.yesterday']);
    const g = groups[groups.length - 1];
    if (g && g.day === day) g.items.push(it);
    else groups.push({ day, items: [it] });
  }

  return (
    <Screen className="history">
      <ScreenHeader title={t['history.title']} onBack={false} />
      <div className="filters" role="tablist" aria-label={t['history.title']}>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`filter${filter === f.id ? ' is-on' : ''}`}
            onClick={() => {
              haptic.select();
              setFilter(f.id);
            }}
          >
            {t[`history.${f.id}`]}
          </button>
        ))}
      </div>
      {q.isPending ? (
        <div className="rows">
          {Array.from({ length: 6 }, (_, i) => (
            <div className="row" key={i}>
              <span className="row__lead">
                <Skeleton w={36} h={36} r={11} />
              </span>
              <span className="row__main">
                <Skeleton w={150} h={14} />
                <Skeleton w={90} h={12} />
              </span>
            </div>
          ))}
        </div>
      ) : q.isError ? (
        <ErrorState text={t['common.error']} onRetry={() => void q.refetch()} />
      ) : items.length === 0 ? (
        <p className="state">{t['history.empty']}</p>
      ) : (
        <>
          {groups.map((g) => (
            <section key={g.day} className="day">
              <h2 className="day__title">{g.day}</h2>
              <div className="rows">
                {g.items.map((a) => (
                  <ActivityRow key={a.id} item={a} />
                ))}
              </div>
            </section>
          ))}
          {q.hasNextPage && (
            <div className="more">
              <Slab tone="quiet" block={false} loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                {t['history.more']}
              </Slab>
            </div>
          )}
        </>
      )}
    </Screen>
  );
}

export function ActivityDetail({ id }: { id: string }) {
  const t = useT();
  const lang = useLang();
  const q = useActivityItem(id);
  const cancel = useMoneyMutation((wid: string) => api(`/withdraw/${wid}/cancel`, { body: {} }));
  const a = q.data;

  return (
    <Screen className="detail">
      <ScreenHeader title={a ? t[`type.${a.type}`] : ''} />
      {q.isPending ? (
        <div className="detail__hero">
          <Skeleton w={180} h={44} r={12} />
        </div>
      ) : q.isError || !a ? (
        <ErrorState text={t['common.error']} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className="detail__hero">
            <AssetChip asset={a.type === 'swap' && a.asset2 ? a.asset2 : a.asset} size={56} />
            <Money
              parts={signedParts(a.type === 'swap' && a.amount2 && a.asset2 ? a.amount2 : a.amount, a.type === 'swap' && a.asset2 ? a.asset2 : a.asset, lang)}
              unit={a.type === 'swap' && a.asset2 ? a.asset2 : a.asset}
              size="xl"
              className={Number(a.amount) > 0 || a.type === 'swap' ? 'is-in' : ''}
            />
            <p className={`status status--${a.status}`}>{t[`status.${a.status}`]}</p>
            <p className="detail__title">{activityTitle(a, t)}</p>
          </div>
          <Facts
            items={[
              a.type === 'swap' && { label: t['swap.give'], value: <span className="num">{formatCrypto(a.amount.replace('-', ''), 9, lang)} {a.asset}</span> },
              { label: t['detail.date'], value: formatDateTime(a.createdAt, lang) },
              a.network && { label: t['detail.network'], value: NETWORKS[a.network].name },
              a.fee && Number(a.fee) > 0 && { label: t['detail.fee'], value: <span className="num">{formatCrypto(a.fee, 9, lang)} {a.asset}</span> },
              a.type === 'deposit' && a.network && a.confirmations !== null && {
                label: t['detail.confirmations'],
                value: <span className="num">{Math.min(a.confirmations, NETWORKS[a.network].confirmations)} / {NETWORKS[a.network].confirmations}</span>,
              },
              a.address && a.address !== 'faucet' && { label: t['detail.address'], value: <span className="mono-address">{a.address}</span> },
              a.comment && { label: t['detail.comment'], value: a.comment },
              {
                label: t['detail.code'],
                value: (
                  <button type="button" className="copyable num" onClick={() => void copyText(a.code).then(() => toast.good(t['deposit.copied']))}>
                    {a.code} <Copy size={14} aria-hidden="true" />
                  </button>
                ),
              },
            ]}
          />
          <div className="detail__actions">
            {a.explorerUrl && (
              <Slab tone="quiet" icon={<ArrowSquareOut size={18} />} onClick={() => openExternal(a.explorerUrl!)}>
                {t['detail.explorer']}
              </Slab>
            )}
            {a.type === 'withdraw' && (a.status === 'pending' || a.status === 'review') && a.refId && (
              <Slab
                tone="danger"
                loading={cancel.isPending}
                onClick={() =>
                  cancel.mutate(a.refId!, {
                    onSuccess: () => {
                      haptic.success();
                      void q.refetch();
                    },
                    onError: (e) => toast.bad(errorText(t, e instanceof ApiError ? e.code : undefined)),
                  })
                }
              >
                {t['detail.cancel']}
              </Slab>
            )}
            <Slab tone="ghost" onClick={() => back('/history')}>
              {t['common.close']}
            </Slab>
          </div>
        </>
      )}
    </Screen>
  );
}
